"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const privacyFeatures = [
  {
    icon: "LockClosedIcon",
    title: "End-to-End Encryption",
    description:
      "All pet data is encrypted in transit and at rest using AES-256 encryption. Your information is protected at every step.",
    color: "#0EA5A0",
    bg: "#E0F7F6",
  },
  {
    icon: "ShieldCheckIcon",
    title: "SOC 2 Compliant",
    description:
      "We follow industry-leading security standards. Our infrastructure is audited regularly to ensure your data stays safe.",
    color: "#7C3AED",
    bg: "#EDE9FE",
  },
  {
    icon: "EyeSlashIcon",
    title: "No Third-Party Sharing",
    description:
      "We never sell or share your pet's data with advertisers, data brokers, or third parties. Your data belongs to you.",
    color: "#0369A1",
    bg: "#E0F2FE",
  },
  {
    icon: "ServerIcon",
    title: "Secure Cloud Storage",
    description:
      "Data is stored on enterprise-grade cloud servers with automatic backups, redundancy, and 99.9% uptime guarantee.",
    color: "#059669",
    bg: "#DCFCE7",
  },
];

export default function DataPrivacy() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Content */}
          <div className="scroll-fade-left">
            <span className="teal-badge mb-4">Data Privacy</span>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] mt-4 mb-6 tracking-tight leading-tight">
              Your pet's data is{" "}
              <span className="gradient-text">locked down tight</span>
            </h2>
            <p className="text-lg text-[#4A6363] leading-relaxed mb-8">
              We built DogFob with privacy as the foundation. Every design decision prioritizes your control, transparency, and security. Here's how we protect your pet's information.
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                <p className="text-[#4A6363] text-sm leading-relaxed">
                  <strong className="text-[#1A2B2B] font-semibold">Zero-knowledge architecture:</strong> We can't access your pet's medical records even if we wanted to.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                <p className="text-[#4A6363] text-sm leading-relaxed">
                  <strong className="text-[#1A2B2B] font-semibold">GDPR & CCPA ready:</strong> Full compliance with global privacy regulations.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="CheckCircleIcon" size={20} className="text-teal-500 flex-shrink-0 mt-0.5" />
                <p className="text-[#4A6363] text-sm leading-relaxed">
                  <strong className="text-[#1A2B2B] font-semibold">Data portability:</strong> Export your pet's full profile anytime in standard formats.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Features */}
          <div className="scroll-fade-right grid sm:grid-cols-2 gap-5">
            {privacyFeatures.map((feature, idx) => (
              <div key={idx} className="feature-card p-6 flex flex-col">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: feature.bg }}
                >
                  <Icon
                    name={feature.icon as "LockClosedIcon"}
                    size={20}
                    style={{ color: feature.color }}
                  />
                </div>
                <h4 className="text-base font-bold text-[#1A2B2B] font-heading mb-2">
                  {feature.title}
                </h4>
                <p className="text-sm text-[#4A6363] leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}